import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../App";

const HRAbsencesPage = () => {
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState({ status: '', absence_type: '', employee_id: '' });
  const [formData, setFormData] = useState({
    absence_type: 'vacation',
    start_date: '',
    end_date: '',
    reason: ''
  });
  const { user } = useAuth();

  const isAdmin = ['super_admin', 'tenant_admin', 'hr_manager'].includes(user?.role);

  useEffect(() => {
    fetchRequests();
    if (isAdmin) fetchEmployees();
  }, [filter]);

  const fetchRequests = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.absence_type) params.append('absence_type', filter.absence_type);
      if (filter.employee_id) params.append('employee_id', filter.employee_id);
      if (isAdmin) params.append('pending_approval', 'true');
      
      const response = await api.get(`/hr/absences?${params.toString()}`);
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching absence requests:', error);
      toast.error('Erro ao carregar pedidos de ausência');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees?limit=500');
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    try {
      await api.post('/hr/absences', formData);
      toast.success('Pedido de ausência criado com sucesso');
      setShowModal(false);
      setFormData({
        absence_type: 'vacation',
        start_date: '',
        end_date: '',
        reason: ''
      });
      fetchRequests();
    } catch (error) {
      console.error('Error creating request:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar pedido');
    }
  };

  const handleApprove = async (requestId, approvalType, action, notes = '') => {
    try {
      await api.put(`/hr/absences/${requestId}/approve?approval_type=${approvalType}&action=${action}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`);
      toast.success(action === 'approve' ? 'Pedido aprovado' : 'Pedido rejeitado');
      fetchRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      toast.error(error.response?.data?.detail || 'Erro ao processar pedido');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30',
      manager_approved: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30',
      hr_approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30',
      rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30',
      cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30'
    };
    const labels = {
      pending: 'Pendente',
      manager_approved: 'Aprovado Chefia',
      hr_approved: 'Aprovado RH',
      rejected: 'Rejeitado',
      cancelled: 'Cancelado'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || colors.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const colors = {
      vacation: 'bg-blue-100 text-blue-700',
      sick_leave: 'bg-red-100 text-red-700',
      justified_absence: 'bg-emerald-100 text-emerald-700',
      unjustified_absence: 'bg-gray-100 text-gray-700',
      parental_leave: 'bg-purple-100 text-purple-700'
    };
    const labels = {
      vacation: 'Férias',
      sick_leave: 'Baixa Médica',
      justified_absence: 'Falta Justificada',
      unjustified_absence: 'Falta Injustificada',
      parental_leave: 'Licença Parental'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[type] || 'bg-gray-100 text-gray-700'}`}>
        {labels[type] || type}
      </span>
    );
  };

  const canApproveAsManager = (request) => {
    return request.status === 'pending' && (isAdmin || request.manager_id === user?.user_id);
  };

  const canApproveAsHR = (request) => {
    return request.status === 'manager_approved' && isAdmin;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const handleExportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.absence_type) params.append('absence_type', filter.absence_type);
      
      const response = await api.get(`/hr/absences/export/excel?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ausencias_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exportado com sucesso');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error('Erro ao exportar Excel');
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.absence_type) params.append('absence_type', filter.absence_type);
      
      const response = await api.get(`/hr/absences/export/pdf?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ausencias_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exportado com sucesso');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erro ao exportar PDF');
    }
  };

  return (
    <div data-testid="hr-absences-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Gestão de Ausências</h1>
          <p className="text-muted-foreground">Pedidos de férias, baixas e faltas</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              <button
                onClick={handleExportExcel}
                data-testid="export-excel-btn"
                className="px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center gap-2 text-sm"
                title="Exportar para Excel"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Excel
              </button>
              <button
                onClick={handleExportPDF}
                data-testid="export-pdf-btn"
                className="px-3 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-2 text-sm"
                title="Exportar para PDF"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                PDF
              </button>
            </>
          )}
          <button
            onClick={() => setShowModal(true)}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Novo Pedido
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-xl border bg-card p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Estado</label>
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
            >
              <option value="">Todos</option>
              <option value="pending">Pendente</option>
              <option value="manager_approved">Aprovado Chefia</option>
              <option value="hr_approved">Aprovado RH</option>
              <option value="rejected">Rejeitado</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Tipo</label>
            <select
              value={filter.absence_type}
              onChange={(e) => setFilter({ ...filter, absence_type: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-input bg-background"
            >
              <option value="">Todos</option>
              <option value="vacation">Férias</option>
              <option value="sick_leave">Baixa Médica</option>
              <option value="justified_absence">Falta Justificada</option>
              <option value="unjustified_absence">Falta Injustificada</option>
              <option value="parental_leave">Licença Parental</option>
            </select>
          </div>
          {isAdmin && (
            <div>
              <label className="block text-sm font-medium mb-1">Colaborador</label>
              <select
                value={filter.employee_id}
                onChange={(e) => setFilter({ ...filter, employee_id: e.target.value })}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background"
              >
                <option value="">Todos</option>
                {employees.map(emp => (
                  <option key={emp.employee_id} value={emp.employee_id}>{emp.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Requests List */}
      <div className="space-y-4">
        {requests.length === 0 ? (
          <div className="rounded-xl border bg-card p-8 text-center">
            <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-muted-foreground">Sem pedidos de ausência</p>
          </div>
        ) : (
          requests.map((request) => (
            <div key={request.request_id} className="rounded-xl border bg-card p-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                    <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold">{request.employee_id}</span>
                      {getTypeBadge(request.absence_type)}
                      {getStatusBadge(request.status)}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {request.start_date} a {request.end_date} ({request.total_days} dias)
                    </p>
                    {request.reason && (
                      <p className="text-sm mt-1">{request.reason}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      Criado em {new Date(request.created_at).toLocaleDateString('pt-PT')}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {canApproveAsManager(request) && (
                    <>
                      <button
                        onClick={() => handleApprove(request.request_id, 'manager', 'approve')}
                        className="px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 text-sm font-medium transition-colors"
                      >
                        Aprovar (Chefia)
                      </button>
                      <button
                        onClick={() => handleApprove(request.request_id, 'manager', 'reject')}
                        className="px-3 py-1.5 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 text-sm font-medium transition-colors"
                      >
                        Rejeitar
                      </button>
                    </>
                  )}
                  {canApproveAsHR(request) && (
                    <>
                      <button
                        onClick={() => handleApprove(request.request_id, 'hr', 'approve')}
                        className="px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 text-sm font-medium transition-colors"
                      >
                        Aprovar (RH)
                      </button>
                      <button
                        onClick={() => handleApprove(request.request_id, 'hr', 'reject')}
                        className="px-3 py-1.5 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 text-sm font-medium transition-colors"
                      >
                        Rejeitar
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Workflow Status */}
              <div className="mt-4 pt-4 border-t border-border">
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${request.status !== 'pending' ? 'bg-emerald-500' : 'bg-gray-300'}`}></div>
                    <span className={request.status !== 'pending' ? 'text-foreground' : 'text-muted-foreground'}>Submetido</span>
                  </div>
                  <div className="flex-1 h-0.5 bg-gray-200"></div>
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${['manager_approved', 'hr_approved'].includes(request.status) ? 'bg-emerald-500' : request.status === 'rejected' ? 'bg-red-500' : 'bg-gray-300'}`}></div>
                    <span className={['manager_approved', 'hr_approved'].includes(request.status) ? 'text-foreground' : 'text-muted-foreground'}>Chefia</span>
                  </div>
                  <div className="flex-1 h-0.5 bg-gray-200"></div>
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${request.status === 'hr_approved' ? 'bg-emerald-500' : request.status === 'rejected' && request.hr_approved_by ? 'bg-red-500' : 'bg-gray-300'}`}></div>
                    <span className={request.status === 'hr_approved' ? 'text-foreground' : 'text-muted-foreground'}>RH</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Request Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl border shadow-lg w-full max-w-md">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold">Novo Pedido de Ausência</h2>
            </div>
            <form onSubmit={handleCreateRequest} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Tipo de Ausência</label>
                <select
                  value={formData.absence_type}
                  onChange={(e) => setFormData({ ...formData, absence_type: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  required
                >
                  <option value="vacation">Férias</option>
                  <option value="sick_leave">Baixa Médica</option>
                  <option value="justified_absence">Falta Justificada</option>
                  <option value="unjustified_absence">Falta Injustificada</option>
                  <option value="parental_leave">Licença Parental</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Data Início</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Data Fim</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Motivo</label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-input bg-background"
                  rows={3}
                  placeholder="Descreva o motivo da ausência..."
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  Submeter Pedido
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HRAbsencesPage;
